import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import {
  getAllOverridesForNamespace,
  setContentOverride,
  removeContentOverride,
  clearAllOverrides,
} from '@/hooks/useContent';
import { TABS, TAB_FIELDS, type TabKey, type FieldDef } from './fieldData';

export default function AdminPage() {
  const { auth, logout } = useAdminAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('hero');
  const [lang, setLang] = useState<'es' | 'en'>('es');
  const [editValues, setEditValues] = useState<Record<string, string>>();
  const [saveMsg, setSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const activeMeta = TABS.find((t) => t.key === activeTab)!;
  const namespace = activeMeta.namespace;
  const fields = TAB_FIELDS[activeTab];

  const getDefaults = useCallback(
    (ns: string, lng: string): Record<string, string> => {
      const bundle = i18n.getResourceBundle(lng, ns);
      return bundle && typeof bundle === 'object' ? (bundle as Record<string, string>) : {};
    },
    [i18n],
  );

  const loadOverrides = useCallback(() => {
    const saved = getAllOverridesForNamespace(lang, namespace);
    const defaults = getDefaults(namespace, lang);
    const merged: Record<string, string> = {};

    fields.forEach((field) => {
      merged[field.key] = saved[field.key] !== undefined ? saved[field.key] : (defaults[field.key] || '');
    });

    Object.keys(saved).forEach((k) => {
      if (!(k in merged)) merged[k] = saved[k];
    });

    setEditValues(merged);
    setHasChanges(false);
  }, [lang, namespace, fields, getDefaults]);

  useEffect(() => {
    if (!auth.isLoggedIn) {
      navigate('/');
    }
  }, [auth.isLoggedIn, navigate]);

  useEffect(() => {
    loadOverrides();
  }, [activeTab, lang, loadOverrides]);

  const handleChange = (key: string, value: string) => {
    setEditValues((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleReset = (key: string) => {
    removeContentOverride(lang, namespace, key);
    const defaults = getDefaults(namespace, lang);
    setEditValues((prev) => ({ ...prev, [key]: defaults[key] || '' }));
    setHasChanges(true);
  };

  const handleSaveAll = () => {
    try {
      const defaults = getDefaults(namespace, lang);
      Object.entries(editValues).forEach(([key, value]) => {
        if (value !== defaults[key]) {
          setContentOverride(lang, namespace, key, value);
        } else {
          removeContentOverride(lang, namespace, key);
        }
      });
      setSaveMsg({ type: 'success', text: 'Cambios guardados correctamente. Recarga la página pública para verlos.' });
      setHasChanges(false);
      setTimeout(() => setSaveMsg(null), 5000);
    } catch {
      setSaveMsg({ type: 'error', text: 'Error al guardar los cambios.' });
      setTimeout(() => setSaveMsg(null), 5000);
    }
  };

  const handleResetAll = () => {
    if (window.confirm('Eliminar TODOS los cambios personalizados de todas las secciones e idiomas?')) {
      clearAllOverrides();
      loadOverrides();
      setSaveMsg({ type: 'success', text: 'Todos los cambios eliminados. Contenido restaurado al original.' });
      setTimeout(() => setSaveMsg(null), 5000);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!auth.isLoggedIn) return null;

  const isMediaTab = activeTab === 'images';

  // Group media fields by section
  const sectionGroups = isMediaTab
    ? fields.reduce<Record<string, FieldDef[]>>((acc, field) => {
        const section = field.section || 'General';
        if (!acc[section]) acc[section] = [];
        acc[section].push(field);
        return acc;
      }, {})
    : null;

  const sectionOrder = ['Inicio', 'Nosotros', 'Servicios', 'Contacto'];
  const sortedSections = sectionGroups
    ? Object.keys(sectionGroups).sort((a, b) => {
        const ai = sectionOrder.indexOf(a);
        const bi = sectionOrder.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      })
    : [];

  return (
    <div className="min-h-screen bg-background-50" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
      {/* Admin Navbar */}
      <header className="bg-background-100 border-b border-background-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500/15 rounded-lg">
              <i className="ri-shield-user-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <span className="text-foreground-50 font-bold text-sm">Panel Admin</span>
              <span className="text-foreground-400 text-xs ml-2">FMO LLC</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="whitespace-nowrap flex items-center gap-1.5 text-foreground-400 hover:text-foreground-50 text-xs font-semibold transition-colors cursor-pointer"
            >
              <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
              Ver Sitio
            </button>
            <button
              onClick={handleLogout}
              className="whitespace-nowrap flex items-center gap-1.5 bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-500 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-line w-4 h-4 flex items-center justify-center"></i>
              Salir
            </button>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-64px)]">
        {/* Sidebar */}
        <aside className="w-60 bg-background-100 border-r border-background-200 hidden lg:flex flex-col shrink-0 overflow-y-auto">
          <nav className="p-3 flex flex-col gap-1">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-primary-500/15 text-primary-500 border border-primary-500/20'
                    : 'text-foreground-400 hover:text-foreground-50 hover:bg-background-200/50'
                }`}
              >
                <i className={`${tab.icon} w-4 h-4 flex items-center justify-center`}></i>
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="mt-auto p-3 border-t border-background-200 space-y-2">
            <div className="bg-background-200/40 rounded-lg p-3">
              <p className="text-foreground-400 text-[10px] leading-relaxed">
                <i className="ri-lightbulb-line w-3 h-3 inline-flex items-center justify-center mr-1"></i>
                Los cambios se reflejan al recargar la página pública.
              </p>
            </div>
            <button
              onClick={handleResetAll}
              className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-500 text-xs font-semibold px-3 py-2 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-restart-line w-4 h-4 flex items-center justify-center"></i>
              Restaurar Todo
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-6">
            {/* Mobile Tab Selector */}
            <div className="lg:hidden mb-6">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value as TabKey)}
                className="w-full bg-background-100 border border-background-200 text-foreground-50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 cursor-pointer"
              >
                {TABS.map((tab) => (
                  <option key={tab.key} value={tab.key}>{tab.label}</option>
                ))}
              </select>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <div>
                <h1 className="text-foreground-50 text-xl font-bold">{activeMeta.label}</h1>
                <p className="text-foreground-400 text-xs mt-0.5">
                  Editando en {lang === 'es' ? 'Español' : 'Inglés'} · Namespace: <span className="text-primary-500 font-mono">{namespace}</span>
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex bg-background-100 border border-background-200 rounded-full p-0.5">
                  <button
                    onClick={() => setLang('es')}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                      lang === 'es' ? 'bg-primary-500 text-white' : 'text-foreground-400 hover:text-foreground-50'
                    }`}
                  >
                    ES
                  </button>
                  <button
                    onClick={() => setLang('en')}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                      lang === 'en' ? 'bg-primary-500 text-white' : 'text-foreground-400 hover:text-foreground-50'
                    }`}
                  >
                    EN
                  </button>
                </div>
                <button
                  onClick={handleSaveAll}
                  disabled={!hasChanges}
                  className={`whitespace-nowrap flex items-center gap-1.5 font-bold px-4 py-2 rounded-full text-xs transition-colors cursor-pointer ${
                    hasChanges
                      ? 'bg-primary-500 hover:bg-primary-600 text-white'
                      : 'bg-background-200/60 text-foreground-500 cursor-not-allowed'
                  }`}
                >
                  <i className="ri-save-line w-4 h-4 flex items-center justify-center"></i>
                  Guardar
                </button>
              </div>
            </div>

            {/* Save message */}
            {saveMsg && (
              <div className={`flex items-center gap-3 rounded-lg px-4 py-3 mb-6 ${
                saveMsg.type === 'success' ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <i className={`${saveMsg.type === 'success' ? 'ri-check-line text-green-400' : 'ri-error-warning-line text-red-400'} w-4 h-4 flex items-center justify-center shrink-0`}></i>
                <p className={`${saveMsg.type === 'success' ? 'text-green-400' : 'text-red-400'} text-xs`}>{saveMsg.text}</p>
              </div>
            )}

            {/* Info for Media/Performance */}
            {(activeTab === 'images' || activeTab === 'stats') && (
              <div className="bg-primary-500/10 border border-primary-500/20 rounded-lg px-4 py-3 mb-6">
                <p className="text-primary-400 text-xs">
                  <i className="ri-information-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
                  {activeTab === 'images'
                    ? 'Reemplaza o elimina cualquier imagen o video del sitio. Pega una nueva URL para cambiar el contenido visual.'
                    : 'Modifica los números y porcentajes que aparecen en la página. Usa solo números para las barras de progreso (0-100).'}
                </p>
              </div>
            )}

            {/* Fields */}
            {isMediaTab && sectionGroups ? (
              <div className="flex flex-col gap-8">
                {sortedSections.map((section) => (
                  <div key={section}>
                    <h2 className="text-foreground-50 text-lg font-bold mb-4 pb-2 border-b border-background-200">
                      {section}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {sectionGroups[section].map((field) => (
                        <AdminMediaField
                          key={field.key}
                          field={field}
                          value={(editValues && editValues[field.key]) || ''}
                          onChange={(v) => handleChange(field.key, v)}
                          onReset={() => handleReset(field.key)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {fields.map((field) => (
                  <AdminField
                    key={field.key}
                    field={field}
                    value={(editValues && editValues[field.key]) || ''}
                    onChange={(v) => handleChange(field.key, v)}
                    onReset={() => handleReset(field.key)}
                  />
                ))}
              </div>
            )}

            {/* Bottom Save */}
            <div className="mt-8 flex items-center justify-between">
              <p className="text-foreground-500 text-xs">
                {fields.length} campo{fields.length !== 1 ? 's' : ''} en esta sección
              </p>
              <button
                onClick={handleSaveAll}
                disabled={!hasChanges}
                className={`whitespace-nowrap flex items-center gap-2 font-bold px-6 py-3 rounded-full text-sm transition-colors cursor-pointer ${
                  hasChanges
                    ? 'bg-primary-500 hover:bg-primary-600 text-white'
                    : 'bg-background-200/60 text-foreground-500 cursor-not-allowed'
                }`}
              >
                <i className="ri-save-line w-5 h-5 flex items-center justify-center"></i>
                Guardar Todos los Cambios
              </button>
            </div>

            <div className="h-20"></div>
          </div>
        </main>
      </div>
    </div>
  );
}

function AdminField({
  field,
  value,
  onChange,
  onReset,
}: {
  field: FieldDef;
  value: string;
  onChange: (v: string) => void;
  onReset: () => void;
}) {
  return (
    <div className="bg-background-100 border border-background-200/50 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <label className="text-foreground-50 text-sm font-semibold">
          {field.label}
          <span className="text-foreground-400 text-[10px] ml-2 font-normal">({field.key})</span>
        </label>
        <button
          onClick={onReset}
          className="whitespace-nowrap text-foreground-400 hover:text-red-400 text-[10px] font-semibold transition-colors cursor-pointer flex items-center gap-1"
          title="Restaurar valor original"
        >
          <i className="ri-arrow-go-back-line w-3 h-3 flex items-center justify-center"></i>
          Original
        </button>
      </div>

      {field.multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-background-50 border border-background-200/50 text-foreground-50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-background-50 transition-all resize-y min-h-[100px]"
          rows={4}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-background-50 border border-background-200/50 text-foreground-50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-background-50 transition-all"
        />
      )}

      <p className="text-foreground-400 text-[10px] mt-2">
        {field.desc || 'Texto mostrado en la página web'}
      </p>
    </div>
  );
}

function AdminMediaField({
  field,
  value,
  onChange,
  onReset,
}: {
  field: FieldDef;
  value: string;
  onChange: (v: string) => void;
  onReset: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isVideo = value.match(/\.(mp4|webm|mov)($|\?)/i);

  const handleReplace = () => {
    inputRef.current?.focus();
    inputRef.current?.select();
  };

  const handleDelete = () => {
    if (window.confirm('Eliminar esta imagen/video? Se borrará la URL del campo.')) {
      onChange('');
    }
  };

  return (
    <div className="bg-background-100 border border-background-200/50 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <label className="text-foreground-50 text-sm font-semibold">
          {field.label}
          <span className="text-foreground-400 text-[10px] ml-2 font-normal">({field.key})</span>
        </label>
        <button
          onClick={onReset}
          className="whitespace-nowrap text-foreground-400 hover:text-red-400 text-[10px] font-semibold transition-colors cursor-pointer flex items-center gap-1"
          title="Restaurar valor original"
        >
          <i className="ri-arrow-go-back-line w-3 h-3 flex items-center justify-center"></i>
          Original
        </button>
      </div>

      {/* Media Preview with overlay buttons */}
      <div className="relative rounded-lg overflow-hidden border border-background-200/50 bg-black/30 mb-3">
        {value ? (
          <>
            <div className="w-full aspect-video">
              {isVideo ? (
                <video
                  src={value}
                  className="w-full h-full object-cover"
                  muted
                  preload="metadata"
                />
              ) : (
                <img
                  src={value}
                  alt={field.label}
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
            </div>
            {/* Overlay buttons */}
            <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
              <button
                onClick={handleReplace}
                className="whitespace-nowrap flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-xs font-bold px-4 py-2.5 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-exchange-line w-4 h-4 flex items-center justify-center"></i>
                Reemplazar
              </button>
              <button
                onClick={handleDelete}
                className="whitespace-nowrap flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-4 py-2.5 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                Eliminar
              </button>
            </div>
          </>
        ) : (
          <div className="w-full aspect-video flex flex-col items-center justify-center gap-2 bg-background-200/20">
            <i className="ri-image-line w-10 h-10 text-foreground-400/30 flex items-center justify-center"></i>
            <p className="text-foreground-500 text-xs">Sin imagen o video</p>
            <button
              onClick={handleReplace}
              className="whitespace-nowrap flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors cursor-pointer mt-1"
            >
              <i className="ri-add-line w-4 h-4 flex items-center justify-center"></i>
              Añadir URL
            </button>
          </div>
        )}
      </div>

      {/* URL Input */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Pega aquí la URL de la imagen o video..."
        className="w-full bg-background-50 border border-background-200/50 text-foreground-50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-background-50 transition-all"
      />

      <p className="text-foreground-400 text-[10px] mt-2">
        {field.desc || 'URL del contenido visual mostrado en la página web'}
      </p>
    </div>
  );
}